import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, Mail, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const LOGO_URL =
   "https://www.ontour.scootive.pl/img/logo.svg";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Zalogowano");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-10 text-black"
      style={{
        background:
          "radial-gradient(120% 60% at 50% -10%, rgba(46,108,46,0.10) 0%, rgba(46,108,46,0.04) 30%, rgba(250,250,250,1) 60%)",
      }}
    >
      <div className="w-full max-w-md rise-in rise-1">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs font-medium text-black/45 hover:text-brand-green mb-6"
        >
          <ArrowLeft className="w-3 h-3" /> Wróć do głosowania
        </Link>

        <div className="flex flex-col items-center text-center mb-7">
          <img src={LOGO_URL} alt="Scootive" className="h-10" />
          <p className="mt-2 text-[10px] font-medium uppercase tracking-[0.4em] text-black/40">
            Strefa zarządzania
          </p>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl uppercase tracking-tight leading-none">
            Panel Admina
          </h1>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="surface-card p-6 sm:p-7 space-y-4"
        >
          <div>
            <label className="block text-[11px] font-medium tracking-wide uppercase text-black/55 mb-2">
              Email
            </label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-black/40" />
              <Input
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 bg-white border-black/10 text-black input-rounded focus-visible:ring-1 focus-visible:ring-[#2e6c2e] focus-visible:border-[#2e6c2e] h-12 shadow-sm"
                placeholder="admin@..."
              />
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-medium tracking-wide uppercase text-black/55 mb-2">
              Hasło
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-black/40" />
              <Input
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 bg-white border-black/10 text-black input-rounded focus-visible:ring-1 focus-visible:ring-[#2e6c2e] focus-visible:border-[#2e6c2e] h-12 shadow-sm"
                placeholder="••••••••"
              />
            </div>
          </div>
          <Button
            type="submit"
            data-testid="admin-login-submit"
            disabled={submitting}
            className="w-full btn-brand h-12 text-base font-semibold tracking-wide gap-2 group mt-2"
          >
            {submitting ? "Logowanie…" : "Wejdź"}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Button>
        </form>
      </div>
    </div>
  );
}
